import { Phone } from "lucide-react";
import { useState, useEffect } from "react";

/**
 * FloatingPhone
 * A sticky bottom-right call-to-action button that:
 * - Pulses on load to draw attention
 * - Expands to show the full phone number on hover / after 3 s
 * - Dials directly on mobile (tel: link)
 * - Hides on very small screens where the number is already in the top bar
 */
export function FloatingPhone() {
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);

  // Fade in after 1.5 s so it doesn't compete with the hero on load
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // Auto-expand once after 3 s to show the number, then collapse
  useEffect(() => {
    if (!visible) return;
    const open  = setTimeout(() => setExpanded(true),  3000);
    const close = setTimeout(() => setExpanded(false), 6500);
    return () => { clearTimeout(open); clearTimeout(close); };
  }, [visible]);

  return (
    <a
      href="tel:+18003016220"
      aria-label="Call Local Ranking Coach at 1-800-301-6220"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        backgroundColor: "#F5C842",
        color: "#1A2B4A",
        borderRadius: "50px",
        padding: expanded ? "14px 22px" : "14px",
        boxShadow: "0 4px 24px rgba(245,200,66,0.45), 0 2px 8px rgba(0,0,0,0.18)",
        textDecoration: "none",
        fontWeight: 700,
        fontSize: "15px",
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        maxWidth: expanded ? "260px" : "52px",
        transition: "max-width 0.35s cubic-bezier(0.23,1,0.32,1), padding 0.35s cubic-bezier(0.23,1,0.32,1), box-shadow 0.2s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transitionProperty: "max-width, padding, box-shadow, opacity, transform",
      }}
    >
      {/* Pulsing ring */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50px",
          border: "2px solid #F5C842",
          animation: "phoneRipple 2.2s ease-out infinite",
          pointerEvents: "none",
        }}
      />

      {/* Phone icon — always visible */}
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: "24px",
          height: "24px",
          animation: "phoneBounce 2.2s ease-in-out infinite",
        }}
      >
        <Phone size={20} strokeWidth={2.5} />
      </span>

      {/* Number text — slides in */}
      <span
        style={{
          opacity: expanded ? 1 : 0,
          transition: "opacity 0.2s ease",
          transitionDelay: expanded ? "0.15s" : "0s",
          fontSize: "15px",
          fontWeight: 700,
          letterSpacing: "0.03em",
        }}
      >
        1-800-301-6220
      </span>

      {/* Keyframe styles injected once */}
      <style>{`
        @keyframes phoneRipple {
          0%   { transform: scale(1);   opacity: 0.6; }
          70%  { transform: scale(1.55); opacity: 0; }
          100% { transform: scale(1.55); opacity: 0; }
        }
        @keyframes phoneBounce {
          0%, 100% { transform: rotate(0deg); }
          10%       { transform: rotate(-15deg); }
          20%       { transform: rotate(15deg); }
          30%       { transform: rotate(-10deg); }
          40%       { transform: rotate(8deg); }
          50%       { transform: rotate(0deg); }
        }
        @media (max-width: 480px) {
          /* On very small phones keep it compact */
          a[aria-label*="1-800-301-6220"] {
            bottom: 16px;
            right: 16px;
          }
        }
      `}</style>
    </a>
  );
}
